import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { IonAlert, IonApp, IonRouterOutlet, IonSplitPane, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import React, { Suspense, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import usePWA from 'react-pwa-install-prompt';
import { Redirect, Route } from 'react-router';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

import AppUrlListener from './AppUrlListener';
import AutoUpdater from './components/AutoUpdater';
import Loading from './components/Loading';
import MenuItems from './components/MenuItem';
import NotFound from './components/NotFound';
import PrivateRoute from './components/PrivateRoute';
import CreateAsset from './pages/Assets/CreateAsset';
import Assets from './pages/Assets/HomeAsset';
import AllAssets from './pages/Assets/ShowAsset';
import AssetDet from './pages/Assets/ViewAsset';
import CompCatalogue from './pages/Component/CompCatalogue';
import CompDetails from './pages/Component/CompDetails';
import CreateComp from './pages/Component/CreateComp';
import ComponentDetail from './pages/Component/componentDetail';
import CreateDocument from './pages/Documents/CreateDocument';
import DocumentsHome from './pages/Documents/DocumentsHome';
import EditDocument from './pages/Documents/EditDocument';
import ViewDocument from './pages/Documents/ViewDocument';
import Home from './pages/Home';
import Login from './pages/Login';
import CreateMainten1 from './pages/Maintenance/CreateMainten1';
import MaintenanceDetail from './pages/Maintenance/MaintenanceDetail';
import MaintenaceHistory from './pages/Maintenance/MaintenanceHistory';
import MaintenanceHome from './pages/Maintenance/MaintenanceHome';
import ModelCreate from './pages/Model937/create';
import ModelDetail from './pages/Model937/detail';
import ModelHistory from './pages/Model937/history';
import ModelHome from './pages/Model937/home';
import NoRecFound from './pages/NoRecFound';
import Offline from './pages/Offline';
import Profile from './pages/Profile';
import CreateProject from './pages/Project/CreateProject';
import CreateTask from './pages/Project/CreateTask';
import HomeProject from './pages/Project/HomeProject';
import ViewAllProjects from './pages/Project/ViewAllProjects';
import ViewlAllTask from './pages/Project/ViewAllTask';
import ViewProject from './pages/Project/ViewProject';
import ViewTask from './pages/Project/ViewTask';
import ResetPassword from './pages/ResetPassword';
import ServiceDetail from './pages/Service/ServiceDetail';
import ServiceHistory from './pages/Service/ServiceHistory';
import ServiceHome from './pages/Service/ServiceHome';
import ServiceCreate from './pages/Service/ServiceManagement';
import Survey from './pages/Survey/CreateSurvey';
import SurveyHome from './pages/Survey/HomeSurvey';
import ShowSurvey from './pages/Survey/ShowSurvey';
import ViewSurvey from './pages/Survey/ViewSurvey';
import { AppContext } from './providers/AppContext';
// import CacheBuster from './CashBuster';


setupIonicReact();

const App: React.FC = () => {



  const { dispatch, state } = useContext(AppContext);
  const { t } = useTranslation();

  // show pwa prompt
  const { isStandalone, isInstallPromptSupported, promptInstall } = usePWA();
  const onClickInstall = async () => {
    await promptInstall();
  };
  if (isInstallPromptSupported && isStandalone) {
    onClickInstall();
  }

  useEffect(() => {
    const configureStatusBar = async () => {
      try {
        await EdgeToEdge.enable();
        await EdgeToEdge.setBackgroundColor({ color: '#1d508d' });
        await StatusBar.setStyle({ style: Style.Dark });
      } catch (err) {
        console.error("StatusBar config error:", err);
      }
    };

    configureStatusBar();

    const favicon = document.getElementById('app-favicon') as HTMLLinkElement;
    if (favicon && process.env.REACT_APP_APP_LOGO) {
      favicon.href = process.env.REACT_APP_APP_LOGO;
    }

    document.title = process.env.REACT_APP_APP_NAME || 'Hyprism';
  }, []);

  return (
    <IonApp>
      <IonReactRouter>
        <Suspense fallback={Loading}>
          <IonSplitPane contentId="main" when="(min-width: 768px)">
            <MenuItems />
            <IonRouterOutlet id="main">
              <PrivateRoute component={ServiceCreate} path="/serviceCreate" exact />
              <PrivateRoute component={MaintenaceHistory} path="/maintenanceHistory" exact />
              <PrivateRoute component={MaintenanceHome} path="/maintenanceHome" exact />
              <PrivateRoute component={Offline} path="/offline" exact />
              <PrivateRoute component={CreateAsset} path="/createAsset" exact />
              <PrivateRoute component={NoRecFound} path="/noRecFound" exact />
              <PrivateRoute component={MaintenanceDetail} path="/maintenanceDetail" exact />
              <PrivateRoute component={Home} path="/home" exact />
              <PrivateRoute component={Assets} path="/assets" exact />
              <PrivateRoute component={AllAssets} path="/all" exact />
              <PrivateRoute component={AssetDet} path="/assetDetail" />
              <PrivateRoute component={CreateComp} path="/createComp" exact />
              <PrivateRoute component={ComponentDetail} path="/comDetail" exact />
              <PrivateRoute component={CompCatalogue} path="/compCatalogue" exact />
              <PrivateRoute component={CreateMainten1} path="/createMainten1" exact />
              <PrivateRoute component={DocumentsHome} path="/documents" exact />
              <PrivateRoute component={CreateDocument} path="/documents/create" exact />
              <PrivateRoute component={EditDocument} path="/documents/edit" exact />
              <PrivateRoute component={ViewDocument} path="/documents/view" exact />
              <PrivateRoute component={Profile} path="/profile" exact />
              <PrivateRoute component={CompDetails} path="/compDetails" exact />
              <PrivateRoute component={ServiceDetail} path="/serviceDetail" exact />
              <PrivateRoute component={ServiceHome} path="/servicehome" exact />
              <PrivateRoute component={ServiceHistory} path="/serviceHistory" exact />
              <PrivateRoute component={MaintenanceDetail} path="/maintainanceDetail" exact />
              <PrivateRoute component={Survey} path="/create-survey" exact />
              <PrivateRoute component={ShowSurvey} path="/show-survey" exact />
              <PrivateRoute component={SurveyHome} path="/survey-home" exact />
              <PrivateRoute component={ViewSurvey} path="/view-survey" exact />
              <PrivateRoute component={CreateProject} path="/create-project" exact />
              <PrivateRoute component={HomeProject} path="/project" exact />
              <PrivateRoute component={ViewAllProjects} path="/project-list" exact />
              <PrivateRoute component={ViewProject} path="/view-project" exact />
              <PrivateRoute component={CreateTask} path="/create-task" exact />
              <PrivateRoute component={ViewlAllTask} path="/view-all-task" exact />
              <PrivateRoute component={ViewTask} path="/view-task" exact />
              <PrivateRoute component={ModelHome} path="/model-home" exact />
              <PrivateRoute component={ModelHistory} path="/model-history" exact />
              <PrivateRoute component={ModelDetail} path="/model-detail" exact />
              <PrivateRoute component={ModelCreate} path="/create-model" exact />

              <Route
                exact
                path="/login"
                render={() => {
                  return state.isLoggedIn ? <Redirect to="/" /> : <Route component={Login} path="/login" />;
                }}
              />
              <Route
                component={ResetPassword}
                exact
                path="/reset-password"
              />
              <Redirect
                exact
                from="/"
                to={state.isLoggedIn ? (state.permissions.home ? '/home' : '/survey-home') : '/login'}
              />
              <Route component={NotFound} />
            </IonRouterOutlet>
          </IonSplitPane>
          <AutoUpdater />
          <IonAlert
            isOpen={state.showOfflineSaveMessage}
            onDidDismiss={() => dispatch({ type: 'toggleOfflineSave', action: {} })}
            header={t('Utils.offline_save_header')}
            message={t('Utils.offline_save_message')}
            buttons={[t('Utils.okay')]}
          />
          <AppUrlListener></AppUrlListener>
        </Suspense>
      </IonReactRouter>
    </IonApp>
  );
};

export default App;
